"use client";

import { CODE_LANGUAGE } from "../contract/models/languages";
import CFooter from "../components/CFooter";
import dynamic from "next/dynamic";
import { ModalsProvider } from "@khol/system/modals";
import { TRPCReactProvider } from "@/trpc/react";
import { ToastTray } from '@khol/system/toasts';
import { HeroUIProvider } from "@heroui/system";
import { UserProvider } from "@/providers/UserProvider";
import { SessionProvider } from "@/components/auth-client";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import React from "react";

const CAppBar = dynamic(() => import("../components/CAppBar"), { ssr: false });

// #region agent log - Debug imports with validation (development only)
if (process.env.NODE_ENV === "development") {
  console.log("[DEBUG] ====== IMPORT CHECK START ======");

  const imports = {
    TRPCReactProvider,
    HeroUIProvider,
    SessionProvider,
    ModalsProvider,
    ToastTray,
    UserProvider,
    CFooter,
    ErrorBoundary,
  };

  Object.entries(imports).forEach(([name, component]) => {
    const type = typeof component;
    const isValid =
      type === "function" ||
      (type === "object" &&
        component !== null &&
        typeof (component as { $$typeof?: unknown }).$$typeof !== "undefined");
    console.log(`[DEBUG] ${name}: type=${type}, valid=${isValid}`, component);
    if (!isValid) {
      console.error(
        `[DEBUG] ❌ INVALID COMPONENT: ${name} is ${type}, expected function`
      );
    }
  });

  console.log("[DEBUG] ====== IMPORT CHECK END ======");
}
// #endregion

interface ProvidersProps {
  children: React.ReactNode;
  lang: CODE_LANGUAGE;
}

// #region agent log - Wrapper to catch specific provider errors (development only)
function DebugWrapper({ name, children }: { name: string; children: React.ReactNode }) {
  if (process.env.NODE_ENV === "development") {
    console.log(`[DEBUG] Rendering: ${name}`);
  }
  return <>{children}</>;
}
// #endregion

export function Providers({ children, lang }: ProvidersProps) {
  // #region agent log - Debug render (development only)
  if (process.env.NODE_ENV === "development") {
    console.log("[DEBUG] Providers function called, lang:", lang);
  }
  // #endregion
  
  return (
    <ErrorBoundary>
      <DebugWrapper name="TRPCReactProvider">
        <TRPCReactProvider>
          <DebugWrapper name="HeroUIProvider">
            <HeroUIProvider>
              <DebugWrapper name="SessionProvider">
                <SessionProvider>
                  <DebugWrapper name="ModalsProvider">
                    <ModalsProvider defaultProps={{ isOpen: true }}>
                      <DebugWrapper name="ToastTray">
                        <ToastTray />
                      </DebugWrapper>
                      <DebugWrapper name="UserProvider">
                        <UserProvider>
                          <div className="flex flex-col">
                            <DebugWrapper name="CAppBar">
                              <CAppBar locale={lang} />
                            </DebugWrapper>
                            <main className="h-fit grow">
                              <div className="container mx-auto mb-28 flex flex-col gap-16 p-4 lg:p-0 lg:pt-8">
                                {children}
                              </div>
                            </main>
                            <DebugWrapper name="CFooter">
                              <CFooter />
                            </DebugWrapper>
                          </div>
                        </UserProvider>
                      </DebugWrapper>
                    </ModalsProvider>
                  </DebugWrapper>
                </SessionProvider>
              </DebugWrapper>
            </HeroUIProvider>
          </DebugWrapper>
        </TRPCReactProvider>
      </DebugWrapper>
    </ErrorBoundary>
  );
} 


